import React, { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Mail, Linkedin, Instagram, MapPin, Phone, ArrowDown, Lock } from "lucide-react";
import { profile as defaultProfile, navItems, stats as defaultStats } from "../mock";
import { fetchContent, fetchProjects, fileUrl } from "../lib/api";
import Sections from "./Sections";
import { Link } from "react-router-dom";

const DEFAULT_MARQUEE = ["Brand Strategy", "Consumer Insight", "Editorial Content", "Paid Social", "Market Research", "Campaign Craft", "Growth", "Storytelling"];

function useReveal(deps = []) {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      className={`fixed top-0 inset-x-0 z-30 transition-[backdrop-filter,background-color,border-color] duration-500 ${
        scrolled
          ? "backdrop-blur-md bg-[#F6F1E8]/80 border-b border-[#1A1714]/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2 group">
          <span className="font-serif-display text-xl md:text-2xl tracking-tight">
            Tatenda<span className="text-[#B5523A]">.</span>
          </span>
        </a>
        <nav className="hidden md:flex items-center gap-8">
          {navItems.map((n) => (
            <a
              key={n.id}
              href={`#${n.id}`}
              className="link-underline text-[13px] tracking-[0.18em] uppercase text-[#1A1714]/80 hover:text-[#1A1714] transition-colors"
            >
              {n.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            title="Admin"
            className="hidden md:inline-flex items-center justify-center w-9 h-9 rounded-full border border-[#1A1714]/20 hover:border-[#1A1714] hover:bg-[#1A1714] hover:text-[#F6F1E8] transition-colors"
          >
            <Lock size={14} />
          </Link>
          <a
            href="#contact"
            className="hidden md:inline-flex items-center gap-2 text-[13px] tracking-[0.18em] uppercase border border-[#1A1714]/30 hover:border-[#1A1714] hover:bg-[#1A1714] hover:text-[#F6F1E8] px-4 py-2 rounded-sm transition-colors duration-300"
          >
            Get in touch <ArrowUpRight size={14} />
          </a>
          <a
            href="#contact"
            className="md:hidden text-[12px] tracking-[0.16em] uppercase border border-[#1A1714]/30 px-3 py-2 rounded-sm"
          >
            Contact
          </a>
        </div>
      </div>
    </header>
  );
};

const Marquee = ({ words }) => {
  const list = (words && words.length ? words : DEFAULT_MARQUEE);
  const line = list.join("  \u2014  ");
  return (
    <div className="relative overflow-hidden border-y border-[#1A1714]/15 bg-[#EFE8DA]">
      <div className="flex whitespace-nowrap py-5 marquee-track">
        {[0, 1].map((k) => (
          <div key={k} className="flex shrink-0 pr-12 font-serif-display text-3xl md:text-5xl italic text-[#1A1714]/85">
            <span>{line}</span>
            <span className="px-12 text-[#B5523A]">·</span>
            <span>{line}</span>
            <span className="px-12 text-[#B5523A]">·</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Hero = ({ profile, stats, cv, copy }) => (
  <section id="top" className="relative pt-28 md:pt-36 pb-16 md:pb-24">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <div className="flex items-center gap-3 text-[12px] tracking-[0.22em] uppercase text-[#1A1714]/60 reveal">
        <span className="inline-block w-8 h-px bg-[#1A1714]/40" />
        {copy?.eyebrow || "Portfolio · 2025"}
      </div>

      <h1 className="reveal mt-8 font-serif-display tracking-[-0.02em] leading-[0.95] text-[14vw] md:text-[10.2vw] lg:text-[9.4vw]">
        {profile.firstName || "Tatenda"}
      </h1>

      <div className="mt-6 md:mt-10 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end">
        <div className="md:col-span-7 reveal">
          <p className="font-serif-display italic text-2xl md:text-3xl leading-[1.25] text-[#1A1714]/90">
            {profile.tagline}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a
              href="#work"
              className="inline-flex items-center gap-2 bg-[#1A1714] text-[#F6F1E8] px-6 py-3 rounded-sm text-[13px] tracking-[0.18em] uppercase hover:bg-[#2c2620] transition-colors"
            >
              {copy?.primaryCta || "View selected work"} <ArrowDown size={14} />
            </a>
            {cv?.url ? (
              <a
                href={fileUrl(cv.url)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-sm text-[13px] tracking-[0.18em] uppercase border border-[#1A1714]/30 hover:border-[#1A1714] transition-colors"
              >
                {copy?.cvCta || "Download CV"} <ArrowUpRight size={14} />
              </a>
            ) : (
              <span className="inline-flex items-center gap-2 px-6 py-3 rounded-sm text-[13px] tracking-[0.18em] uppercase border border-[#1A1714]/15 text-[#1A1714]/40 cursor-not-allowed">
                {copy?.cvComingSoon || "CV coming soon"}
              </span>
            )}
          </div>
        </div>

        <div className="md:col-span-5 reveal">
          <div className="grid grid-cols-2 gap-x-6 gap-y-8 md:pl-10 md:border-l border-[#1A1714]/15">
            {stats.map((s, i) => (
              <div key={i}>
                <div className="font-serif-display text-4xl md:text-5xl text-[#1A1714]">{s.value}</div>
                <div className="mt-2 text-[12px] tracking-[0.16em] uppercase text-[#1A1714]/60 leading-[1.5]">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-20 flex items-center gap-4 text-[12px] tracking-[0.22em] uppercase text-[#1A1714]/55 reveal">
        <MapPin size={14} className="text-[#B5523A]" />
        {profile.location}
      </div>
    </div>
  </section>
);

const Footer = ({ profile, copy }) => (
  <footer className="border-t border-[#1A1714]/15 mt-12">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
      <div className="font-serif-display text-2xl tracking-tight">
        Tatenda<span className="text-[#B5523A]">.</span>
      </div>
      <div className="flex flex-wrap items-center gap-6 text-[12px] tracking-[0.18em] uppercase text-[#1A1714]/65">
        {profile.email && (
          <a href={`mailto:${profile.email}`} className="link-underline inline-flex items-center gap-2">
            <Mail size={14} /> Email
          </a>
        )}
        {profile.linkedin && (
          <a href={profile.linkedin} className="link-underline inline-flex items-center gap-2" target="_blank" rel="noreferrer">
            <Linkedin size={14} /> LinkedIn
          </a>
        )}
        {profile.instagram && (
          <a href={profile.instagram} className="link-underline inline-flex items-center gap-2" target="_blank" rel="noreferrer">
            <Instagram size={14} /> Instagram
          </a>
        )}
        {profile.phone && (
          <span className="inline-flex items-center gap-2">
            <Phone size={14} /> {profile.phone}
          </span>
        )}
      </div>
      <div className="text-[11px] tracking-[0.18em] uppercase text-[#1A1714]/50">
        © {new Date().getFullYear()} {profile.firstName || "Tatenda"} · {copy?.tagline || "Built with care"}
      </div>
    </div>
  </footer>
);

export default function Portfolio() {
  const [content, setContent] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, p] = await Promise.all([fetchContent(), fetchProjects()]);
        if (!cancelled) {
          setContent(c);
          setProjects(p);
        }
      } catch (e) {
        // Fallback to mock if backend unreachable
        if (!cancelled) {
          setContent({
            profile: defaultProfile,
            stats: defaultStats,
            skills: [],
            experience: [],
            education: [],
            cv: null,
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useReveal([content, projects]);

  if (loading || !content) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#1A1714]/60 text-sm tracking-[0.2em] uppercase">
        Loading portfolio…
      </div>
    );
  }

  const profile = { ...defaultProfile, ...(content.profile || {}) };
  const stats = content.stats?.length ? content.stats : defaultStats;
  const copy = content.copy || {};

  return (
    <div className="relative">
      <Nav />
      <main className="relative z-10">
        <Hero profile={profile} stats={stats} cv={content.cv} copy={copy.hero} />
        <Marquee words={copy.marquee} />
        <Sections content={content} projects={projects} profile={profile} copy={copy} />
      </main>
      <Footer profile={profile} copy={copy.footer} />
    </div>
  );
}
